import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import useTimeSheet from '@ubeya/shared/hooks/account/useTimeSheet';
import useAccountConfig from '@ubeya/shared/hooks/account/useAccountConfig';
import moment from 'moment';
import { API_DATE_FORMAT } from '@ubeya/shared/constants';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useLabels from '@ubeya/shared/hooks/account/useLabels';
import useLocations from '@ubeya/shared/hooks/account/useLocations';
import useJobTypes from '@ubeya/shared/hooks/generic/useJobTypes';
import usePtoTypes from '@ubeya/shared/hooks/generic/usePtoTypes';
import usePositions from '@ubeya/shared/hooks/account/usePositions';
import useAdmins from '@ubeya/shared/hooks/account/useAdmins';
import useTags from '@ubeya/shared/hooks/account/useTags';
import useTimesheetFields from '@ubeya/shared/hooks/account/useTimesheetFields';
import groupByData from 'shared/utils/timesheet';
import { TIMESHEET_TABLE_CONTEXT } from 'shared/constants/timeSheet';
import TimeSheetTableGrid from 'timeSheet/components/TimeSheetTableGrid/TimeSheetTableGrid';

const StyledTimeSheetTableGrid = styled(TimeSheetTableGrid)`
  padding: 0;
  margin-top: 8px;
`;

const JobTimesheet = ({
  employeeIds,
  showEmployeeRating,
  nameColumnDateSort,
  timeRange,
  clickableDate = false,
  addDummy,
  defaultSort = { sortBy: 'name', sortDirection: 'asc' },
  amplitudeModuleName = 'Timesheet module'
}) => {
  const [sort, setSort] = useState(defaultSort);
  const {
    account: { payrollConfig }
  } = useAccountConfig();

  const { branches, isLoading: branchesLoading } = useBranches();
  const { isLoading: accountLoading } = useAccount();
  const { isLoading: labelsLoading } = useLabels();
  const { isLoading: locationsLoading } = useLocations();
  const { isLoading: jobTypesLoading } = useJobTypes();
  const { isLoading: ptoTypesLoading } = usePtoTypes();
  const { isLoading: positionsLoading } = usePositions();
  const { isLoading: adminsLoading } = useAdmins();
  const { isLoading: tagsLoading } = useTags();
  const { isLoading: timesheetFieldsLoading } = useTimesheetFields();

  const isMappersAndFieldsLoading =
    accountLoading ||
    branchesLoading ||
    labelsLoading ||
    locationsLoading ||
    jobTypesLoading ||
    ptoTypesLoading ||
    positionsLoading ||
    adminsLoading ||
    tagsLoading ||
    timesheetFieldsLoading;

  const [sortingColumns, onSortingColumnsChanged] = useState({});
  const {
    isLoading,
    data: timesheetData,
    updateStartTime,
    updateEndTime,
    approveTimeSheet,
    approveAllTimeSheet,
    updateShiftDetails,
    deleteTimeSheet,
    autofill,
    updateBreak,
    createDailyShift,
    deleteBreak,
    updateEmployeeRating,
    isBreakTimesDisplayed,
    refetch
  } = useTimeSheet({ timeRange, employeeIds, withJobs: true });

  const slots = useMemo(
    () =>
      (timesheetData.slots || []).map((slot) => ({
        ...slot,
        date: slot?.startTime ? moment(slot.startTime).format(API_DATE_FORMAT) : slot.date
      })),
    [timesheetData.slots]
  );

  const showCheckInOutColumns = useMemo(
    () => (branches || []).some((branch) => !!branch?.timesheetConfig?.toRequireCheckInWithMobile),
    [branches]
  );

  const data = useMemo(() => {
    const groupedData = groupByData({
      timeRange,
      jobs: timesheetData?.jobs,
      slots,
      employees: timesheetData?.employees,
      groupByKey: 'employee',
      sort,
      sortingColumns,
      addDummy,
      nameColumnDateSort: false,
      deductBreaktimesIsActive: payrollConfig?.deductBreaktimesIsActive || false
    });
    return groupedData?.[0]?.subRows || [];
  }, [
    timeRange,
    timesheetData?.jobs,
    timesheetData?.employees,
    slots,
    sort,
    sortingColumns,
    addDummy,
    payrollConfig?.deductBreaktimesIsActive
  ]);

  const timesheetProps = useMemo(
    () => ({
      isLoading: isLoading || isMappersAndFieldsLoading,
      data,
      employeeIds,
      refetchTimesheet: refetch,
      updateStartTime,
      showEmployeeRating,
      nameColumnDateSort,
      updateEndTime,
      approveTimeSheet,
      clickableDate,
      updateEmployeeRating,
      approveAllTimeSheet,
      updateShiftDetails,
      deleteTimeSheet,
      autofill,
      sort,
      setSort,
      updateBreak,
      createDailyShift,
      deleteBreak,
      onSortingColumnsChanged,
      isBreakTimesDisplayed,
      timeRange,
      groupBy: 'employee',
      tableContext: TIMESHEET_TABLE_CONTEXT.EMPLOYEE_JOB_TAB,
      isStickyDrawer: false,
      showTotalTimeWithoutBreaksColumn: payrollConfig?.deductBreaktimesIsActive,
      shadow: false,
      isGroupedData: false,
      showCheckInOutColumns,
      amplitudeModuleName
    }),
    [
      isLoading,
      isMappersAndFieldsLoading,
      data,
      employeeIds,
      refetch,
      updateStartTime,
      showEmployeeRating,
      nameColumnDateSort,
      updateEndTime,
      approveTimeSheet,
      clickableDate,
      updateEmployeeRating,
      approveAllTimeSheet,
      updateShiftDetails,
      deleteTimeSheet,
      autofill,
      sort,
      updateBreak,
      createDailyShift,
      deleteBreak,
      isBreakTimesDisplayed,
      timeRange,
      payrollConfig?.deductBreaktimesIsActive,
      showCheckInOutColumns,
      amplitudeModuleName
    ]
  );

  return <StyledTimeSheetTableGrid timesheetProps={timesheetProps} />;
};

export default JobTimesheet;
